<script setup lang="ts">
import CouponSubtotals from '@/components/business/coupons/CouponSubtotals.vue'
import TextButton from '@/components/business/TextButton.vue'

const { t } = useI18n()

const { formatCurrency } = useCurrency()
const { paymentRepository } = useApi()

const { id: businessId, isSMGDiscountApplied, isDiscountActive } = storeToRefs(useBusinessStore())

const userStore = useUserStore()
const widgetStore = useWidgetStore()

const props = defineProps<{
  billingCycle: BillingCycle
  existingDiscountPromoCode: string | null
  pricePlan: PricePlan
  priceData: PaymentPreview | null
  isFetchingPrice: boolean
}>()

const emit = defineEmits<{
  'apply-coupon': [promoCode: string]
  'remove-coupon': []
}>()

const inputValue = ref('')
watch(inputValue, newInputValue => {
  if (newInputValue !== '') inputErrors.value = null
})
watch(
  () => props.billingCycle,
  () => {
    if (couponAppliedToBillingCycle.value === props.billingCycle) emit('apply-coupon', couponCode.value)
    inputValue.value = ''
    inputErrors.value = null
  }
)

const loading = ref(false)
const couponAppliedToBillingCycle = ref('')
const couponCode = ref('')
const inputErrors = ref<string[] | null>(null)
const isReferral = ref(false)

const hasAppliedCoupon = computed(() => props.existingDiscountPromoCode || isReferral.value)

const showApplyButton = computed(() => inputValue.value !== '' && !loading.value)

const showPromoCodeInput = computed(() => !isDiscountActive.value && !isReferral.value)

const totalDiscountAmountAndCurrency = computed(() => {
  if (props.priceData) return formatCurrency(Number(props.priceData.amountDiscountTotal), props.priceData.currency)
  return ''
})

const isPercentageDiscount = computed(() => {
  return Boolean(props.priceData && props.priceData.percentOff > 0)
})

const percentDiscountText = computed(() => {
  if (props.priceData) return `-${props.priceData.percentOff}%`
  return ''
})

const discountAmountText = computed(() => {
  if (isPercentageDiscount.value) return percentDiscountText.value
  return totalDiscountAmountAndCurrency.value
})

const showDiscountIcon = computed(() => {
  return !showPromoCodeInput.value
})

const applyCoupon = async () => {
  loading.value = true
  couponAppliedToBillingCycle.value = ''
  couponCode.value = inputValue.value
  try {
    const { valid } = await paymentRepository.validateCoupon(businessId.value, couponCode.value)
    if (valid) {
      emit('apply-coupon', couponCode.value)
      couponAppliedToBillingCycle.value = props.billingCycle
      widgetStore.createSnackbar({ message: t('business.coupon.snackbar_success') })
      inputValue.value = ''
    } else {
      emit('remove-coupon')
      inputErrors.value = [t('global.general_error')]
    }
  } catch {
    widgetStore.createSnackbar({
      message: t('global.general_error'),
    })
  } finally {
    loading.value = false
  }
}

const removeCoupon = () => {
  couponAppliedToBillingCycle.value = ''
  couponCode.value = ''
  emit('remove-coupon')
}

watch(
  () => props.existingDiscountPromoCode,
  () => {
    if (props.existingDiscountPromoCode && !isSMGDiscountApplied.value) {
      inputValue.value = props.existingDiscountPromoCode
      void applyCoupon()
    }
  }
)

onMounted(() => {
  // if user has registered through referral (they will have a discount)
  const appliedReferralCode = userStore.appliedReferralCode
  if (appliedReferralCode) {
    couponCode.value = appliedReferralCode
    isReferral.value = true
    widgetStore.createSnackbar({ message: t('business.coupon.snackbar_success') })
  }

  if (props.existingDiscountPromoCode && !isSMGDiscountApplied.value) {
    inputValue.value = props.existingDiscountPromoCode
    void applyCoupon()
  }
})
</script>

<template>
  <div class="coupon-input d-flex flex-column" data-cy="coupon_input_component">
    <div>
      <v-text-field
        v-if="showPromoCodeInput"
        v-model="inputValue"
        prepend-icon="custom:coupon_tag"
        :placeholder="t('business.coupon.input.placeholder')"
        variant="outlined"
        class="coupon-input__input"
        :disabled="loading"
        :error-messages="inputErrors"
        :hide-details="!inputErrors"
        single-line
        autocapitalize="characters"
        data-cy="coupon_input"
        @keyup.enter="showApplyButton ? applyCoupon() : null"
      >
        <template #append-inner>
          <text-button
            v-if="showApplyButton"
            data-cy="apply_coupon_button"
            color="primary"
            block
            @click="applyCoupon()"
          >
            {{ t('business.coupon.input.apply') }}
          </text-button>
          <skr-spinner v-if="loading" :size="24" />
        </template>
      </v-text-field>
    </div>
    <div
      v-if="couponAppliedToBillingCycle === billingCycle || hasAppliedCoupon"
      :class="['coupon-input__coupon-applied', { 'ml-10 pt-4': showPromoCodeInput }]"
    >
      <v-banner two-line rounded data-cy="coupon_applied_banner" class="coupon-input__banner | pl-4">
        <template v-if="showDiscountIcon" #prepend>
          <v-avatar size="20">
            <v-icon :size="16">custom:coupon_tag</v-icon>
          </v-avatar>
        </template>
        <strong>{{ couponCode || existingDiscountPromoCode }}</strong
        >&nbsp;{{ t('business.coupon.input.banner_applied') }}&nbsp;
        <span v-if="Boolean(priceData?.amountDiscountTotal)"> ({{ discountAmountText }})</span>
        <template v-if="showPromoCodeInput" #actions>
          <v-btn icon data-cy="remove_applied_coupon" :aria-label="t('global.remove')" @click="removeCoupon()">
            <v-icon>custom:e_remove</v-icon>
          </v-btn>
        </template>
      </v-banner>
      <coupon-subtotals
        :coupon-code="couponCode || existingDiscountPromoCode || ''"
        :price-data="priceData"
        :is-fetching-price="isFetchingPrice"
      />
    </div>
  </div>
</template>

<style lang="sass">
$ns: coupon-input
.#{$ns}
  &__input
    font-size: 15px
    font-weight: bold
    ::placeholder
      font-weight: normal
    .v-field__slot input
      color: $c-skribbleu
    fieldset
      border-width: 2px
      border-radius: 6px
      border-color: $c-grey-fine
    .v-input__prepend-outer
      margin-right: 16px
    .v-input__append-inner
      margin-top: 0px
      align-self: center

  &__coupon-applied
    .v-banner
      border-radius: 6px
      font-size: 14px
      display: flex
      justify-content: space-between
      align-items: center
      padding-top: 15px
      height: 48px
      .v-banner__prepend
        margin-right: 3 * $bw
        align-self: center
      .v-banner__content
        align-items: center
        font-size: 14px
        color: $c-skribbleu
        margin-right: auto
      .v-banner-actions
        margin-top: 0px !important
        flex: 0 1 0 !important
        margin-bottom: 0 !important
        padding-top: 0 !important
        align-self: center

.coupon-input__banner
  background-color: #E1FFF6 !important
  border-bottom: none !important
</style>
